import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, X, ShoppingBag } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useCart } from "@/context/CartContext";

const Logo = () => (
  <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
    <div className="flex items-center justify-center w-11 h-11 rounded-full border border-gold/40 bg-gradient-to-br from-ivory to-[#F3E4BC] group-hover:border-gold transition-colors duration-300">
      <svg viewBox="0 0 24 34" className="w-4 h-6">
        <rect x="9.5" y="0" width="5" height="34" rx="1.2" fill="#C9A13A" />
        <rect x="2" y="8" width="20" height="5" rx="1.2" fill="#C9A13A" />
      </svg>
    </div>
    <div className="leading-tight">
      <p className="font-heading text-base sm:text-lg font-semibold text-brown-black tracking-wide">PROPHÈTE</p>
      <p className="font-heading text-base sm:text-lg font-semibold gold-gradient-text -mt-1 tracking-wide">STEEVE ORPHÔSS</p>
      <p className="text-[9px] uppercase tracking-[0.25em] text-brown-dark/60 -mt-0.5">Ministère International</p>
    </div>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { count, openCart } = useCart();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-ivory/90 backdrop-blur-xl border-b border-gold/20" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-brown-black/80 hover:text-gold"
                  } after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={openCart} className="relative p-2 text-brown-black/80 hover:text-gold transition-colors" data-testid="open-cart-button" aria-label="Panier">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-gold rounded-full" data-testid="navbar-cart-count">
                  {count}
                </span>
              )}
            </button>
            <Link
              to="/faire-un-don"
              data-testid="navbar-donate-button"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-white text-sm font-semibold tracking-wide shadow-md shadow-gold/20 hover:bg-gold-light hover:shadow-lg transition-all duration-300"
            >
              <Heart size={15} fill="currentColor" />
              Faire un don
            </Link>
            <button
              className="lg:hidden p-2 text-brown-black"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gold/20 bg-ivory" data-testid="mobile-menu">
          <nav className="px-6 py-5 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-md font-medium ${isActive ? "text-gold bg-gold/5" : "text-brown-black/80"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/faire-un-don"
              onClick={() => setOpen(false)}
              data-testid="mobile-donate-button"
              className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gold text-white font-semibold"
            >
              <Heart size={16} fill="currentColor" /> Faire un don
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
